import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBanners } from './banners';
import { Banner } from '../types/banner';

const getFolderPathFromLocation = (pathname: string): string | undefined => {
  const rawPath = pathname.replace('/library', '').replace(/^\//, '');
  return rawPath ? decodeURIComponent(rawPath) : undefined;
};

interface LibraryContextType {
  banners: Banner[];
  folders: string[];
  currentFolder: string | undefined;
  isLoading: boolean;
}

const LibraryContext = createContext<LibraryContextType | undefined>(undefined);

export const LibraryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { getBannersByFolder, folders } = useBanners();

  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Cargar banners cuando cambia la URL
  useEffect(() => {
    const loadBanners = async () => {
      setIsLoading(true);
      const folderPath = getFolderPathFromLocation(location.pathname);
      setCurrentFolder(folderPath);

      const data = await getBannersByFolder(folderPath);
      setBanners(data);
      setIsLoading(false);
    };

    loadBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const value: LibraryContextType = {
    banners,
    folders,
    currentFolder,
    isLoading
  };

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
};

export const useLibrary = (): LibraryContextType => {
  const context = useContext(LibraryContext);
  if (context === undefined) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  return context;
};
